/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	ChangeDetectionStrategy,
	OnInit,
	ViewChild,
	ChangeDetectorRef,
	OnDestroy,
} from '@angular/core';
import {
	ContextMenuService,
	CONTEXT_MENUS,
	IContextMenu,
	IContextMenuData,
} from '../../context-menu.service';
import { Subject } from 'rxjs';
import {
	QUICK_STATUS_COMPLETE_AS_PLANNED,
	QUICK_STATUS_COMPLETE_WITH_CHANGES,
	QUICK_STATUS_MARK_AS_STARTED,
} from '@touchplan/strings';
import { ActualPhase, Ticket, TicketType } from '@touchplan/common';
import { takeUntil } from 'rxjs/operators';
import { PlanState } from 'src/app/services/plan-state.service';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { MatMenuTrigger } from '@angular/material/menu';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';

@Component({
	selector: 'ticket-quick-status-prompt',
	templateUrl: './ticket-quick-status-prompt.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketQuickStatusPromptComponent implements OnInit, OnDestroy, IContextMenu {
	@ViewChild('contextMenu', { static: true }) public contextMenu;
	@ViewChild('contextMenuTrigger') public trigger: MatMenuTrigger;
	public ticketCountLabel = '';
	public completeAsPlannedStr = QUICK_STATUS_COMPLETE_AS_PLANNED;
	public completeWithChangesStr = QUICK_STATUS_COMPLETE_WITH_CHANGES;
	public markAsStartedStr = QUICK_STATUS_MARK_AS_STARTED;
	public menuData$: Subject<IContextMenuData>;
	private ticketContextData: {
		tickets: Map<string, Ticket>;
	};
	public disableCompleteWithChanges: boolean;
	public disableMarkAsStarted: boolean;
	public hideMarkAsStarted: boolean;
	private shutdownSubject: Subject<boolean> = new Subject();
	constructor(
		private planState: PlanState,
		private contextMenuService: ContextMenuService,
		private cd: ChangeDetectorRef,
		private planAccessCheck: PlanAccessCheck
	) {}
	public ngOnInit() {
		this.menuData$ = this.contextMenuService.register(CONTEXT_MENUS.TICKET_QUICK_STATUS, this);
		this.menuData$.pipe(takeUntil(this.shutdownSubject)).subscribe((menuData) => {
			if (menuData) {
				this.ticketContextData = menuData.data;
				// Set string label for context menu
				this.ticketCountLabel =
					this.ticketContextData.tickets.size > 1 ? ` ${this.ticketContextData.tickets.size}` : '';
				this.completeAsPlannedStr = `${QUICK_STATUS_COMPLETE_AS_PLANNED}${this.ticketCountLabel}`;
				this.completeWithChangesStr = `${QUICK_STATUS_COMPLETE_WITH_CHANGES}${this.ticketCountLabel}`;
				this.markAsStartedStr = `${QUICK_STATUS_MARK_AS_STARTED}${this.ticketCountLabel}`;

				const isConstraintAndPlanLocked: boolean =
					this.isConstraintType(this.ticketContextData.tickets) &&
					this.planAccessCheck.hasRestrictedAccessSync;
				// MOC-4302 - Special handling for constraints, until panel is modernized.
				this.disableCompleteWithChanges =
					isConstraintAndPlanLocked ||
					(this.ticketContextData &&
						this.ticketContextData.tickets &&
						this.ticketContextData.tickets.size > 1);

				this.disableMarkAsStarted =
					this.isConstraintType(this.ticketContextData?.tickets) ||
					this.hasStartedTickets(this.ticketContextData?.tickets) ||
					this.hasMilestoneTickets(this.ticketContextData?.tickets);

				this.hideMarkAsStarted =
					(this.hasMilestoneTickets(this.ticketContextData?.tickets) &&
						!this.hasTasks(this.ticketContextData?.tickets)) ||
					(this.isConstraintType(this.ticketContextData?.tickets) &&
						!this.hasTasks(this.ticketContextData?.tickets));

				//could regen the whole thing to trigger the object changed change detection, but this is easier
				this.cd.markForCheck();
			}
		});

		// Everytime new ticket is edited, close quick status
		this.planState.tickets.editTicket$.pipe(takeUntil(this.shutdownSubject)).subscribe((ticketsInEdit) => {
			this.contextMenuService.hide(CONTEXT_MENUS.TICKET_QUICK_STATUS);
		});
	}

	public ngOnDestroy() {
		this.shutdownSubject.next(true);
		this.contextMenuService.remove(CONTEXT_MENUS.TICKET_QUICK_STATUS);
		this.ticketContextData = null;
		this.ticketCountLabel = '';
	}

	public completeAsPlannedClicked(): void {
		if (!this.ticketContextData.tickets) {
			return;
		}
		this.ticketContextData.tickets.forEach((ticket) => {
			const newData = ticket.convertPlannedToActual();
			this.planState.actions.updateTicketLiveData(ticket.$id, newData);
			this.planState.actions.persistTicketData(ticket);
			this.planState.actions.unSelectTicket(ticket);
			// Note the changes as a segment events:
			analyticsEventsService.fastCompleteAsPlanned(this.planState.planId, ticket.$id);
			analyticsEventsService.updateStatusButtonClicked(this.planState.planId, ticket.$id);
		});
	}

	public completeWithChanges(): void {
		if (!this.ticketContextData.tickets) {
			return;
		}
		// let shouldOpenEdit = true;

		this.ticketContextData.tickets.forEach((ticket) => {
			// MOC-4048 - Don't override actualPhase for constraints
			// Complete with Changes is greyed out for multi-tickets
			if (ticket.view.type === TicketType.Constraint) {
				this.openMoreDetails(ticket);
				// shouldOpenEdit = false;
				return;
			}
			const newData = ticket.convertPlannedToActual();
			newData['actualPhaseOverride'] = ActualPhase.actualizing;
			this.planState.actions.changeTicketViewToEdit(ticket);
			this.planState.actions.updateTicketLiveData(ticket.$id, newData);

			analyticsEventsService.fastCompleteWithChanges(this.planState.planId, ticket.$id);
		});

		// if (shouldOpenEdit) {
		// 	this.planState.actions.changeTicketViewToEdit(this.ticketContextData.tickets);
		// }
	}

	public onMarkAsStartedClicked(): void {
		if (!this.ticketContextData.tickets) {
			return;
		}
		this.ticketContextData.tickets.forEach((ticket) => {
			const newData = ticket.convertPlannedToStarted();
			this.planState.actions.updateTicketLiveData(ticket.$id, newData);
			this.planState.actions.persistTicketData(ticket);
			this.planState.actions.unSelectTicket(ticket);
			analyticsEventsService.fastStartWork(this.planState.planId, ticket.$id);
		});
	}

	private isConstraintType(tickets): boolean {
		if (!tickets) {
			return false;
		}
		let result = false;
		tickets.forEach((ticket, key) => {
			if (ticket.view.type === TicketType.Constraint) {
				result = true;
			}
		});
		return result;
	}

	private hasMilestoneTickets(tickets: Map<string, Ticket>): boolean {
		const ticketsArray = !!tickets ? Array.from(tickets) : [];
		return ticketsArray.some(([_, ticket]) => ticket.view.type === TicketType.Milestone);
	}

	private hasStartedTickets(tickets: Map<string, Ticket>): boolean {
		const ticketsArray = !!tickets ? Array.from(tickets) : [];
		return ticketsArray.some(([_, ticket]) => ticket.view.actualPhase === ActualPhase.started);
	}

	private hasTasks(tickets: Map<string, Ticket>): boolean {
		const ticketsArray = !!tickets ? Array.from(tickets) : [];
		return ticketsArray.some(([_, ticket]) => ticket.view.type === TicketType.Task || !ticket.view.type);
	}

	private openMoreDetails(ticket: Ticket): void {
		const assembledId: string = this.planState.planId + ',' + ticket.$id;
		this.planState.legacyActions.openConstraintNotePanel(assembledId);
	}

	public onChanged(newData: IContextMenuData): void {
		this.menuData$.next(newData);
	}
}
